/** @jsxImportSource @emotion/react */
import React, { memo, useMemo } from 'react';
import { css } from '@emotion/react';
// import { marked } from 'marked';
import Markdown, { type Options as MarkdownOptions } from 'react-markdown';
import remarkDirective from 'remark-directive';
import remarkFlexibleMarkers from 'remark-flexible-markers';
import remarkGfm from 'remark-gfm';
import { remarkCitations } from './remark.citations-plugin';
import { remarkCustomComponents } from './remark.custom-component-plugin';
import { remarkDirectiveToComponent, remarkCollapsible } from './remark.directive-component-plugin';
import {
  SecureHtmlRenderer,
  parseContent,
  mixedContentContainerStyles,
  markdownBlockStyles,
  htmlBlockStyles,
} from './SecureHtmlRenderer';
import { useMarkdownComponents } from './useMarkdownComponents';

export interface SecureHtmlOptions {

  adaptiveHeight?: boolean;
}

export interface ChatMarkdownProps {
  components?: MarkdownOptions['components'];
  value: string;
  allowHtml?: boolean;
  trustedDomains?: string[];
  htmlOptions?: SecureHtmlOptions;
}

// Shared remark plugins configuration
const REMARK_PLUGINS = [
  remarkGfm,
  remarkFlexibleMarkers,
  remarkDirective,
  remarkDirectiveToComponent,
  remarkCitations,
  remarkCustomComponents,
  remarkCollapsible,
];

/**
 * Markdown renderer with support for mixed markdown/HTML content
 * Supports secure rendering of HTML blocks with domain validation and CSP
 */
export const ChatMarkdown: React.FC<ChatMarkdownProps> = ({
  components: customComponents,
  value,
  allowHtml = false,
  trustedDomains,
  htmlOptions,
}: ChatMarkdownProps) => {
  const components = useMarkdownComponents(customComponents);

  if (!allowHtml) {
    return (
      <Markdown
        css={markdownStyles}
        remarkPlugins={REMARK_PLUGINS}
        skipHtml={true}
        components={components}
      >
        {value}
      </Markdown>
    );
  }

  const contentBlocks = parseContent(value);
  if (contentBlocks.length === 1 && contentBlocks[0].type === 'markdown') {
    return (
      <Markdown
        css={markdownStyles}
        remarkPlugins={REMARK_PLUGINS}
        skipHtml={false}
        components={components}
      >
        {contentBlocks[0].content}
      </Markdown>
    );
  }

  return (
    <div css={mixedContentContainerStyles}>
      {contentBlocks.map((block, index) => (
        <ContentBlock
          key={`${block.type}-${index}`}
          block={block}
          components={components}
          trustedDomains={trustedDomains}
          htmlOptions={htmlOptions}
        />
      ))}
    </div>
  );
};

interface ContentBlockProps {
  block: { type: 'markdown' | 'html'; content: string };
  components: MarkdownOptions['components'];
  trustedDomains?: string[];
  htmlOptions?: SecureHtmlOptions;
}

const ContentBlock = ({ block, components, trustedDomains, htmlOptions }: ContentBlockProps) => {
  if (block.type === 'markdown') {
    return (
      <div css={markdownBlockStyles}>
        <Markdown
          css={markdownStyles}
          remarkPlugins={REMARK_PLUGINS}
          skipHtml={false}
          components={components}
        >
          {block.content}
        </Markdown>
      </div>
    );
  }

  return (
    <div css={htmlBlockStyles}>
      <SecureHtmlRenderer
        html={block.content}
        trustedDomains={trustedDomains}
        adaptiveHeight={htmlOptions?.adaptiveHeight}
      />
    </div>
  );
};

/**
 * Basic markdown renderer without HTML support
 * Lightweight version for simple markdown content
 */
export const BasicMarkdown = ({ children }: { children: string }) => {
  const components = useMarkdownComponents();

  return (
    <Markdown
      remarkPlugins={[remarkCustomComponents]}
      components={components}
    >
      {children}
    </Markdown>
  );
};

const markdownStyles = css({
  whiteSpace: 'normal',
});